import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { walletAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Wallet, 
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  Smartphone,
  Building2,
  Loader2,
  Clock,
  Check
} from 'lucide-react';
import { formatCurrency, formatDateTime } from '../lib/utils';

const paymentMethods = [
  { id: 'd17', name: 'D17', icon: Smartphone, description: 'Mobile Zahlung' },
  { id: 'flouci', name: 'Flouci', icon: Smartphone, description: 'Mobile Wallet' },
  { id: 'bank_card', name: 'Bankkarte', icon: CreditCard, description: 'Visa/Mastercard' },
];

const quickAmounts = [20, 50, 100, 200];

export default function WalletPage() {
  const { user, refreshUser } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState('50');
  const [paymentMethod, setPaymentMethod] = useState('d17');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await walletAPI.getTransactions();
        setTransactions(response.data || []);
      } catch (error) {
        console.error('Failed to fetch transactions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  const handleTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Bitte gib einen gültigen Betrag ein');
      return;
    }

    setProcessing(true);
    try {
      await walletAPI.topup(amount, paymentMethod);
      toast.success(`${formatCurrency(amount)} erfolgreich aufgeladen!`);
      await refreshUser();
      const response = await walletAPI.getTransactions();
      setTransactions(response.data || []);
      setTopupOpen(false);
      setTopupAmount('50');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Aufladung fehlgeschlagen');
    } finally {
      setProcessing(false);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'topup':
        return <ArrowDownRight className="w-5 h-5 text-green-500" />;
      case 'escrow_lock':
        return <Clock className="w-5 h-5 text-amber-500" />;
      case 'escrow_release':
        return <ArrowUpRight className="w-5 h-5 text-tunis-teal" />;
      case 'payment':
        return <ArrowUpRight className="w-5 h-5 text-red-500" />;
      default:
        return <Wallet className="w-5 h-5 text-slate-500" />;
    }
  };

  const getTransactionLabel = (type) => {
    switch (type) {
      case 'topup':
        return 'Aufladung';
      case 'escrow_lock':
        return 'Reserviert';
      case 'escrow_release':
        return 'Zahlung erhalten';
      case 'payment':
        return 'Zahlung';
      case 'commission':
        return 'Gebühr';
      case 'refund':
        return 'Rückerstattung';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-48 rounded-2xl mb-6" />
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="wallet-page">
        {/* Balance Card */}
        <Card className="bg-gradient-to-br from-tunis-blue to-tunis-blue/80 text-white border-0 mb-6" data-testid="balance-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
                <Wallet className="w-7 h-7 text-white" />
              </div>
              <Badge className="bg-white/20 text-white border-0">TND</Badge>
            </div>
            <div className="text-white/70 mb-1">Aktuelles Guthaben</div>
            <div className="font-display font-bold text-4xl mb-6">
              {formatCurrency(user?.wallet_balance || 0)}
            </div>
            
            <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
              <DialogTrigger asChild>
                <Button className="w-full bg-tunis-teal hover:bg-tunis-teal/90 text-white font-bold h-12" data-testid="topup-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  Guthaben aufladen
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Guthaben aufladen</DialogTitle>
                </DialogHeader>
                <div className="space-y-6 pt-4">
                  {/* Quick Amounts */}
                  <div className="space-y-2">
                    <Label>Betrag wählen</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {quickAmounts.map((amount) => (
                        <Button
                          key={amount}
                          variant={topupAmount === amount.toString() ? 'default' : 'outline'}
                          className={topupAmount === amount.toString() ? 'bg-tunis-teal hover:bg-tunis-teal/90' : ''}
                          onClick={() => setTopupAmount(amount.toString())}
                          data-testid={`amount-${amount}`}
                        >
                          {amount} TND
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Amount */}
                  <div className="space-y-2">
                    <Label>Oder eigenen Betrag eingeben</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        min="1"
                        value={topupAmount}
                        onChange={(e) => setTopupAmount(e.target.value)}
                        className="h-12 pr-16"
                        data-testid="custom-amount"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 font-medium text-muted-foreground">
                        TND
                      </span>
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div className="space-y-2">
                    <Label>Zahlungsmethode</Label>
                    <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                      {paymentMethods.map((method) => {
                        const Icon = method.icon;
                        return (
                          <Label
                            key={method.id}
                            htmlFor={method.id}
                            className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              paymentMethod === method.id
                                ? 'border-tunis-teal bg-tunis-teal/5'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <RadioGroupItem value={method.id} id={method.id} data-testid={`method-${method.id}`} />
                            <Icon className="w-6 h-6 text-tunis-blue" />
                            <div className="flex-1">
                              <div className="font-medium">{method.name}</div>
                              <div className="text-sm text-muted-foreground">{method.description}</div>
                            </div>
                          </Label>
                        );
                      })}
                    </RadioGroup>
                  </div>

                  {/* Mock Notice */}
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm">
                    <p className="text-amber-800">
                      <strong>Demo-Modus:</strong> Dies ist eine simulierte Zahlung. In der Produktionsversion werden echte Zahlungsanbieter (D17, Flouci) integriert.
                    </p>
                  </div>

                  {/* Submit */}
                  <Button
                    className="w-full btn-primary h-12"
                    onClick={handleTopup}
                    disabled={processing}
                    data-testid="confirm-topup-btn"
                  >
                    {processing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        {formatCurrency(parseFloat(topupAmount) || 0)} aufladen
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        {/* Transactions */}
        <Card className="border-slate-200" data-testid="transactions-card">
          <CardHeader>
            <CardTitle className="text-tunis-blue">Transaktionsverlauf</CardTitle>
          </CardHeader>
          <CardContent>
            {transactions.length > 0 ? (
              <div className="space-y-3">
                {transactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl"
                    data-testid={`transaction-${transaction.id}`}
                  >
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                      {getTransactionIcon(transaction.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-tunis-blue truncate">
                        {transaction.description}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatDateTime(transaction.created_at)} • {getTransactionLabel(transaction.type)}
                      </div>
                    </div>
                    <div className={`font-bold ${transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {transaction.amount >= 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Wallet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-muted-foreground">Noch keine Transaktionen</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
